import React, { useState, useEffect } from 'react';
import {
    Box,
    Button,
    Card,
    CardContent,
    Container,
    Grid,
    Typography,
    Switch,
    Stack,
    useTheme,
    Chip,
    CircularProgress,
    Accordion,
    AccordionSummary,
    AccordionDetails
} from '@mui/material';
import {
    Check as CheckIcon,
    Extension as ExtensionIcon,
    Mail as MailIcon,
    AutoAwesome as AIIcon,
    QueryStats as StatsIcon,
    LocationOn as LocationIcon,
    ExpandMore as ExpandMoreIcon
} from '@mui/icons-material';
import PaymentProcessor from '../components/PaymentProcessor';

const Pricing = () => {
    const theme = useTheme();
    const [annually, setAnnually] = useState(false);
    const [userCountry, setUserCountry] = useState(null);
    const [loading, setLoading] = useState(true);
    const [paymentOpen, setPaymentOpen] = useState(false);
    const [selectedPlan, setSelectedPlan] = useState(null);

    useEffect(() => {
        const detectLocation = async () => {
            try {
                const response = await fetch('https://ipapi.co/json/');
                const data = await response.json();
                setUserCountry(data.country);
            } catch (error) {
                console.error('Error detecting location:', error);
                setUserCountry('US'); // Default to US pricing
            } finally {
                setLoading(false);
            }
        };

        detectLocation();
    }, []);

    const handleGetStarted = (plan) => {
        if (plan.title === "Basic") {
            // Handle free plan signup
            // You might want to redirect to a signup page
            return;
        }
        setSelectedPlan(plan.title);
        setPaymentOpen(true);
    };

    const getPrice = (basePrice) => {
        if (userCountry === 'IN') {
            // Convert to INR and apply Indian pricing
            if (basePrice === 2.79) return 220
            else if (basePrice === 5.29) return 450
            else if (basePrice === 2.5) return 200
            else if (basePrice === 4.75) return 405
            else return basePrice === 0 ? 0 : Math.round(basePrice * 80);
            // Rough conversion rate
        }
        return basePrice;
    };

    const getCurrencySymbol = () => userCountry === 'IN' ? '₹' : '$';

    const plans = [
        {
            title: "Basic",
            price: getPrice(annually ? 0 : 0),
            period: annually ? "/month" : "/month",
            description: "Perfect for getting started",
            emailLimit: "Find 5 recruiter emails/week",
            featured: false,
            buttonVariant: "outlined",
            features: [
                "Chrome Extension Access",
                "AI Email Generation",
                "Email Automation",
                "Unlimited Email Sends",
                "Gmail/Outlook Integration",
                "Find 5 Recruiter Emails/Week"
            ]
        },
        // {
        //     title: "Pro",
        //     price: getPrice(annually ? 2.5 : 2.79),
        //     period: annually ? "/month" : "/month",
        //     description: "Best for active job seekers",
        //     emailLimit: "Find 20 recruiter emails/week",
        //     featured: true,
        //     buttonVariant: "contained",
        //     features: [
        //         "Chrome Extension Access",
        //         "AI Email Generation",
        //         "Email Automation",
        //         "Unlimited Email Sends",
        //         "Gmail/Outlook Integration",
        //         "Find 20 Recruiter Emails/Week",
        //         "Priority Support"
        //     ]
        // },
        // {
        //     title: "Master",
        //     price: getPrice(annually ? 4.75: 5.29),
        //     period: annually ? "/month" : "/month",
        //     description: "For power networkers",
        //     emailLimit: "Find 40 recruiter emails/week",
        //     featured: false,
        //     buttonVariant: "outlined",
        //     features: [
        //         "Chrome Extension Access",
        //         "AI Email Generation",
        //         "Email Automation",
        //         "Unlimited Email Sends",
        //         "Gmail/Outlook Integration",
        //         "Find 40 Recruiter Emails/Week",
        //         "Priority Support",
        //         // "Advanced Analytics"
        //     ]
        // }
    ];

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '80vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box
            sx={{
                bgcolor: theme.palette.grey[50],
                pt: 4,
                pb: 12
            }}
        >
            <Container maxWidth="lg">
                {/* Header */}
                <Box textAlign="center" mb={8}>
                    <Typography
                        variant="h2"
                        component="h1"
                        fontWeight="bold"
                        mb={2}
                        sx={{ color: theme.palette.text.primary }}
                    >
                        Simple, Transparent Pricing
                    </Typography>
                    <Typography
                        variant="h6"
                        color="text.secondary"
                        mb={2}
                    >
                        Choose the perfect plan for your job search journey
                    </Typography>

                    {/* Location Indicator */}
                    <Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        justifyContent="center"
                        mb={3}
                    >
                        <LocationIcon color="primary" />
                        <Typography color="primary">
                            Showing prices for {userCountry === 'IN' ? 'India' : 'United States'}
                        </Typography>
                    </Stack>

                    {/* Billing Toggle */}
                    {/* <Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        justifyContent="center"
                    >
                        <Typography color={!annually ? 'primary' : 'text.secondary'}>
                            Monthly
                        </Typography>
                        <Switch
                            checked={annually}
                            onChange={() => setAnnually(!annually)}
                            color="primary"
                        />
                        <Typography color={annually ? 'primary' : 'text.secondary'}>
                            Annually
                            <Chip
                                label="Save Upto 10%"
                                size="small"
                                color="primary"
                                sx={{ ml: 1 }}
                            />
                        </Typography>
                    </Stack> */}
                </Box>

                {/* Pricing Cards */}
                <Grid container spacing={4} alignItems="stretch">
                    {plans.map((plan) => (
                        <Grid item xs={12} md={4} key={plan.title}>
                            <Card
                                sx={{
                                    height: '100%',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    position: 'relative',
                                    transition: 'transform 0.2s',
                                    ...(plan.featured && {
                                        border: `2px solid ${theme.palette.primary.main}`,
                                        transform: 'scale(1.05)',
                                    }),
                                    '&:hover': {
                                        transform: plan.featured ? 'scale(1.05)' : 'scale(1.02)',
                                        boxShadow: theme.shadows[10]
                                    }
                                }}
                            >
                                {plan.featured && (
                                    <Chip
                                        label="Most Popular"
                                        color="primary"
                                        sx={{
                                            position: 'absolute',
                                            top: -12,
                                            right: 20,
                                        }}
                                    />
                                )}
                                <CardContent sx={{ p: 4, flexGrow: 1 }}>
                                    <Typography variant="h5" component="h2" fontWeight="bold" gutterBottom>
                                        {plan.title}
                                    </Typography>
                                    <Typography color="text.secondary" paragraph>
                                        {plan.description}
                                    </Typography>
                                    <Box mb={4}>
                                        <Typography variant="h3" component="p" fontWeight="bold">
                                            {getCurrencySymbol()}{plan.price}
                                            <Typography
                                                component="span"
                                                variant="h6"
                                                color="text.secondary"
                                            >
                                                {plan.period}
                                            </Typography>
                                        </Typography>
                                    </Box>

                                    <Stack spacing={2}>
                                        {plan.features.map((feature) => (
                                            <Stack
                                                direction="row"
                                                spacing={2}
                                                alignItems="center"
                                                key={feature}
                                            >
                                                <CheckIcon color="primary" />
                                                <Typography>{feature}</Typography>
                                            </Stack>
                                        ))}
                                    </Stack>

                                    <Button
                                        variant={plan.buttonVariant}
                                        color="primary"
                                        size="large"
                                        fullWidth
                                        sx={{ mt: 4 }}
                                        onClick={() => handleGetStarted(plan)}
                                    >
                                        {plan.title === "Basic" ? "Sign Up Free" : "Get Started"}
                                    </Button>
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                </Grid>

                {/* Features Summary */}
                <Box mt={12} textAlign="center">
                    <Typography variant="h4" component="h3" mb={6}>
                        All Plans Include
                    </Typography>
                    <Grid container spacing={4} justifyContent="center">
                        <Grid item xs={12} sm={6} md={3}>
                            <ExtensionIcon color="primary" sx={{ fontSize: 40, mb: 2 }} />
                            <Typography variant="h6" gutterBottom>
                                Chrome Extension
                            </Typography>
                            <Typography color="text.secondary">
                                Find recruiter emails easily
                            </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <AIIcon color="primary" sx={{ fontSize: 40, mb: 2 }} />
                            <Typography variant="h6" gutterBottom>
                                AI Email Generation
                            </Typography>
                            <Typography color="text.secondary">
                                Craft perfect outreach emails
                            </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <MailIcon color="primary" sx={{ fontSize: 40, mb: 2 }} />
                            <Typography variant="h6" gutterBottom>
                                Email Automation
                            </Typography>
                            <Typography color="text.secondary">
                                Automated follow-ups
                            </Typography>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <StatsIcon color="primary" sx={{ fontSize: 40, mb: 2 }} />
                            <Typography variant="h6" gutterBottom>
                                Email Analytics
                            </Typography>
                            <Typography color="text.secondary">
                                Track your outreach success
                            </Typography>
                        </Grid>
                    </Grid>
                </Box>

                {/* <FAQ></FAQ> */}
                <Box mt={12}>
                    <Typography variant="h4" component="h3" mb={6} textAlign="center">
                        Frequently Asked Questions
                    </Typography>
                    <Box maxWidth="md" mx="auto">
                        <Accordion>
                            <AccordionSummary
                                expandIcon={<ExpandMoreIcon />}
                                aria-controls="panel1a-content"
                                id="panel1a-header"
                            >
                                <Typography variant="h6">
                                    What exactly am I paying for?
                                </Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                <Typography color="text.secondary">
                                    The only paid feature is the number of recruiter emails you can find using our Chrome extension per week.
                                    All other features including AI email generation, email automation, and unlimited email sends are completely free.
                                </Typography>
                            </AccordionDetails>
                        </Accordion>

                        <Accordion>
                            <AccordionSummary
                                expandIcon={<ExpandMoreIcon />}
                                aria-controls="panel2a-content"
                                id="panel2a-header"
                            >
                                <Typography variant="h6">
                                    What if the email finder doesn't work?
                                </Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                <Typography color="text.secondary">
                                    While we strive to provide accurate email addresses, we cannot guarantee 100% accuracy. We are not liable for any
                                    unsuccessful email deliveries or related issues. Our service is provided "as is" without any warranties. We recommend
                                    verifying critical emails before sending important communications.
                                </Typography>
                            </AccordionDetails>
                        </Accordion>

                        <Accordion>
                            <AccordionSummary
                                expandIcon={<ExpandMoreIcon />}
                                aria-controls="panel3a-content"
                                id="panel3a-header"
                            >
                                <Typography variant="h6">
                                    How does the weekly limit work?
                                </Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                <Typography color="text.secondary">
                                    The weekly limit resets every Monday at 12:00 AM UTC. Unused searches don't roll over to the next week.
                                    Basic plan includes 10 searches/week, Pro plan includes 20 searches/week, and Enterprise plan includes 40 searches/week.
                                </Typography>
                            </AccordionDetails>
                        </Accordion>

                        <Accordion>
                            <AccordionSummary
                                expandIcon={<ExpandMoreIcon />}
                                aria-controls="panel4a-content"
                                id="panel4a-header"
                            >
                                <Typography variant="h6">
                                    Can I upgrade or downgrade anytime?
                                </Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                <Typography color="text.secondary">
                                    Yes, you can change your plan at any time. When upgrading, you'll get immediate access to the higher limit.
                                    When downgrading, the new limit will take effect in the next billing cycle. Your existing automations and
                                    data will remain intact regardless of your plan.
                                </Typography>
                            </AccordionDetails>
                        </Accordion>
                    </Box>
                </Box>
                <PaymentProcessor
                    open={paymentOpen}
                    onClose={() => setPaymentOpen(false)}
                    plan={selectedPlan}
                    isAnnual={annually}
                />
            </Container>
        </Box>
    );
};

export default Pricing;